import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    Plus, Search, Monitor, Edit, Trash2, MapPin, 
    User, Key, Eye, EyeOff, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LocalPCs() {
    const { isAdmin } = useAuth();
    const [pcs, setPCs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPC, setEditingPC] = useState(null);
    const [showPasswords, setShowPasswords] = useState({});

    const [formData, setFormData] = useState({
        name: '',
        location: '',
        user_name: '',
        username: '',
        password: '',
        ip_address: '',
        mac_address: '',
        department: '',
        os: '',
        notes: '',
    });

    const departments = [
        'Ön Büro', 'Muhasebe', 'İnsan Kaynakları', 'Satış', 'IT', 
        'Housekeeping', 'F&B', 'Güvenlik', 'Teknik', 'Yönetim', 'Diğer'
    ];

    useEffect(() => {
        loadPCs();
    }, []);

    const loadPCs = async (searchTerm = '') => {
        try {
            const token = localStorage.getItem('token');
            const url = searchTerm 
                ? `${API_URL}/api/local-pcs?search=${encodeURIComponent(searchTerm)}`
                : `${API_URL}/api/local-pcs`;
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPCs(data);
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Bilgisayar listesi yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        loadPCs(value);
    };

    const togglePassword = (id) => {
        setShowPasswords(prev => ({...prev, [id]: !prev[id]}));
    };

    const openCreateDialog = () => {
        setEditingPC(null);
        setFormData({
            name: '',
            location: '',
            user_name: '',
            username: '',
            password: '',
            ip_address: '',
            mac_address: '',
            department: '',
            os: '',
            notes: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (pc) => {
        setEditingPC(pc);
        setFormData({
            name: pc.name || '',
            location: pc.location || '',
            user_name: pc.user_name || '',
            username: pc.username || '',
            password: pc.password || '',
            ip_address: pc.ip_address || '',
            mac_address: pc.mac_address || '',
            department: pc.department || '',
            os: pc.os || '',
            notes: pc.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.location) {
            toast.error('Bilgisayar adı ve konum zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingPC 
                ? `${API_URL}/api/local-pcs/${editingPC.id}`
                : `${API_URL}/api/local-pcs`;
            
            const res = await fetch(url, {
                method: editingPC ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (res.ok) {
                toast.success(editingPC ? 'Bilgisayar güncellendi' : 'Bilgisayar eklendi');
                setDialogOpen(false);
                loadPCs(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (pc) => {
        if (!window.confirm(`"${pc.name}" bilgisayarını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/local-pcs/${pc.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success('Bilgisayar silindi');
                loadPCs(search);
            } else {
                toast.error('Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="local-pcs-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Local Bilgisayar Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {pcs.length} bilgisayar kayıtlı
                    </p>
                </div>
                {isAdmin && (
                    <Button 
                        onClick={openCreateDialog}
                        className="bg-orwion-green hover:bg-orwion-green-hover" 
                        data-testid="create-pc-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Yeni Bilgisayar
                    </Button>
                )}
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Bilgisayar adı, konum, kullanıcı, IP veya MAC ile ara..."
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* PC List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">Bilgisayar Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Konum</th>
                            <th className="text-left p-4 font-medium text-slate-600">Kullanıcı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Kullanıcı Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Şifre</th>
                            <th className="text-left p-4 font-medium text-slate-600">IP Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">MAC Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">Departman</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pcs.length === 0 ? (
                            <tr>
                                <td colSpan="9" className="p-8 text-center text-slate-500">
                                    <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz bilgisayar kaydı yok</p>
                                </td>
                            </tr>
                        ) : (
                            pcs.map((pc) => (
                                <tr key={pc.id} className="border-b hover:bg-slate-50">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <Monitor className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium">{pc.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <MapPin className="w-3 h-3" />
                                            {pc.location}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <User className="w-3 h-3" />
                                            {pc.user_name || '-'}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                            {pc.username || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                                {showPasswords[pc.id] ? (pc.password || '-') : '••••••'}
                                            </code>
                                            {pc.password && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6"
                                                    onClick={() => togglePassword(pc.id)}
                                                >
                                                    {showPasswords[pc.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                            {pc.ip_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                                            {pc.mac_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <Badge variant="outline">
                                            {pc.department || '-'}
                                        </Badge>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                onClick={() => openEditDialog(pc)}
                                                data-testid={`edit-pc-${pc.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="text-red-500 hover:text-red-600"
                                                onClick={() => handleDelete(pc)}
                                                data-testid={`delete-pc-${pc.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingPC ? 'Bilgisayar Düzenle' : 'Yeni Bilgisayar Ekle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Bilgisayar Adı *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Örn: RECEPTION-PC01"
                                data-testid="pc-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Konum *</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({...formData, location: e.target.value})}
                                placeholder="Örn: Resepsiyon"
                                data-testid="pc-location-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Kullanıcı (Kişi)</Label>
                            <Input
                                value={formData.user_name}
                                onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                                placeholder="Örn: Ahmet Yılmaz"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Departman</Label>
                            <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v})}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Departman seçin" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map(dept => (
                                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Windows Kullanıcı Adı</Label>
                            <Input
                                value={formData.username}
                                onChange={(e) => setFormData({...formData, username: e.target.value})}
                                placeholder="Örn: admin"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Windows Şifresi</Label>
                            <Input
                                value={formData.password}
                                onChange={(e) => setFormData({...formData, password: e.target.value})}
                                placeholder="••••••"
                                type="password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi</Label>
                            <Input
                                value={formData.ip_address}
                                onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                                placeholder="Örn: 192.168.1.100"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>MAC Adresi</Label>
                            <Input
                                value={formData.mac_address}
                                onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                                placeholder="Örn: AA:BB:CC:DD:EE:FF"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>İşletim Sistemi</Label>
                            <Input
                                value={formData.os}
                                onChange={(e) => setFormData({...formData, os: e.target.value})}
                                placeholder="Örn: Windows 11 Pro"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                            data-testid="save-pc-btn"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingPC ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default LocalPCs;
